import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Webcam from 'react-webcam';
import { apiService } from '../../services/apiService';
import {
  MicrophoneIcon,
  StopIcon,
  ForwardIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const DirectInterview = () => {
  const { token } = useParams();
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // States
  const [isLoading, setIsLoading] = useState(true);
  const [interviewInfo, setInterviewInfo] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [submittedAnswers, setSubmittedAnswers] = useState(new Set());
  const [isSubmittingAudio, setIsSubmittingAudio] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [error, setError] = useState(null);

  // Load interview info
  useEffect(() => {
    const loadInterviewInfo = async () => {
      try {
        setIsLoading(true);
        const data = await apiService.getDirectInterviewInfo(token);
        setInterviewInfo(data);
        
        // If session already exists, set up for continuation
        if (data.session) {
          setSessionId(data.session.id);
          if (data.session.status === 'in_progress') {
            setInterviewStarted(true);
            await loadProgress(data.session.id);
          }
        }
      } catch (err) {
        setError(err.message);
        toast.error(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      loadInterviewInfo();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load progress
  const loadProgress = async (sessionId) => {
    try {
      const progress = await apiService.getDirectProgress(sessionId);
      setProgressData(progress);
      setCurrentQuestionIndex(progress.current_question || 0);
      
      // Load current question
      if (progress.current_question < progress.total_questions) {
        await loadCurrentQuestion(sessionId, progress.current_question);
      }
    } catch (err) {
      console.error('Failed to load progress:', err);
    }
  };

  // Load current question
  const loadCurrentQuestion = async (sessionId, questionIndex) => {
    try {
      const question = await apiService.getDirectQuestion(sessionId, questionIndex);
      setCurrentQuestion(question);
    } catch (err) {
      if (err.message.includes('not found')) {
        // No more questions
        handleCompleteInterview();
      } else {
        toast.error('Failed to load question');
      }
    }
  };

  // Start interview
  const handleStartInterview = async () => {
    try {
      const sessionData = await apiService.startDirectInterview(token);
      setSessionId(sessionData.session_id);
      setInterviewStarted(true);
      setCurrentQuestionIndex(0);
      
      // Load first question
      await loadCurrentQuestion(sessionData.session_id, 0);
      await loadProgress(sessionData.session_id);
      
      toast.success('Interview started!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Recording timer
  useEffect(() => {
    let interval = null;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(time => time + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
    } catch (error) {
      toast.error('Failed to start recording. Please check microphone permissions.');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/wav' });
        handleSubmitAnswer(audioBlob);
      };

      // Stop all tracks
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    }
  };

  // Submit answer
  const handleSubmitAnswer = async (audioBlob = null) => {
    if (!currentQuestion) {
      toast.error('No current question available');
      return;
    }

    try {
      setIsSubmittingAudio(true);

      if (audioBlob) {
        // Submit with audio
        const formData = new FormData();
        formData.append('question_id', currentQuestion.id);
        formData.append('answer_text', transcript || 'Audio response provided');
        formData.append('transcript', transcript || '');
        formData.append('auto_transcribe', 'true');
        formData.append('audio', audioBlob, `answer_${currentQuestion.id}.wav`);
        
        await apiService.submitDirectAnswerWithSpeech(sessionId, formData);
      } else {
        // Submit text only
        await apiService.submitDirectAnswer(
          sessionId,
          currentQuestion.id,
          transcript || 'No answer provided'
        );
      }

      // Success
      toast.success('Answer submitted successfully!');
      setSubmittedAnswers(prev => new Set([...prev, currentQuestion.id]));
      setTranscript('');
      handleNextQuestion();
      
    } catch (error) {
      toast.error(`Failed to submit: ${error.message}`);
    } finally {
      setIsSubmittingAudio(false);
    }
  };

  // Move to next question
  const handleNextQuestion = async () => {
    const nextIndex = currentQuestionIndex + 1;
    const totalQuestions = progressData?.total_questions || interviewInfo?.interview?.total_questions || 0;
    
    if (nextIndex < totalQuestions) {
      setCurrentQuestion(null);
      setCurrentQuestionIndex(nextIndex);
      await loadCurrentQuestion(sessionId, nextIndex);
      await loadProgress(sessionId);
    } else {
      handleCompleteInterview();
    }
  };

  // Complete interview
  const handleCompleteInterview = async () => {
    try {
      await apiService.completeDirectInterview(sessionId);
      toast.success('Interview completed successfully!');
      
      // Show completion message
      setInterviewStarted(false);
      setCurrentQuestion(null);
      
      // After a brief delay, check if the link is now invalid and refresh the page
      setTimeout(() => {
        checkLinkStatusAndRefresh();
      }, 3000); // Wait 3 seconds to show completion message
      
    } catch (error) {
      toast.error('Failed to complete interview');
    }
  };

  // Check if link is invalid and refresh page
  const checkLinkStatusAndRefresh = async () => {
    try {
      await apiService.getDirectInterviewInfo(token);
    } catch (err) {
      // If the link is now invalid, refresh the page to show the error screen
      if (err.message.includes('invalid') || err.message.includes('used') || err.message.includes('expired')) {
        window.location.reload();
      }
    }
  };

  // Skip question
  const handleSkipQuestion = async () => {
    await handleSubmitAnswer();
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Loading interview...</p>
        </div>
      </div>
    );
  }

  if (error || !interviewInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-900 via-purple-900 to-pink-900">
        <div className="text-center max-w-md mx-4">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Interview Link Invalid</h2>
          <p className="text-red-200 mb-6">
            {error || 'This interview link is invalid, expired, or has already been used.'}
          </p>
          {/* <button
            onClick={() => window.close()}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium"
          >
            Please Close this Window
          </button> */}
        </div>
      </div>
    );
  }

  // Pre-interview screen
  if (!interviewStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white/10 backdrop-blur-lg rounded-lg p-8 text-center border border-white/20">
            <h1 className="text-3xl font-bold text-white mb-2">
              {interviewInfo.interview?.title}
            </h1>
            <p className="text-xl text-white/80 mb-6">
              {interviewInfo.interview?.description}
            </p>
            
            <div className="bg-white/20 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-medium text-white mb-4">Interview Details:</h3>
              <div className="text-white/90 space-y-2 text-left">
                <p><strong>Candidate:</strong> {interviewInfo.candidate?.name}</p>
                <p><strong>Duration:</strong> {interviewInfo.interview?.duration_minutes} minutes</p>
                <p><strong>Questions:</strong> {interviewInfo.interview?.total_questions}</p>
                <p><strong>Link expires:</strong> {new Date(interviewInfo.link?.expires_at).toLocaleString()}</p>
              </div>
            </div>

            <div className="mb-8">
              <Webcam
                ref={webcamRef}
                audio={false}
                className="w-64 h-48 rounded-lg mx-auto border-4 border-white/30"
                mirrored={true}
              />
              <p className="text-white/70 text-sm mt-2">
                Please ensure your camera and microphone are working
              </p>
            </div>

            <button
              onClick={handleStartInterview}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-medium text-lg transition-colors duration-200"
            >
              Start Interview
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Interview completed screen
  if (interviewStarted && !currentQuestion && progressData?.can_complete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-blue-900 to-teal-900 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white/10 backdrop-blur-lg rounded-lg p-8 text-center border border-white/20">
            <CheckIcon className="h-16 w-16 text-green-400 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-4">Interview Completed!</h1>
            <p className="text-xl text-white/80 mb-6">
              Thank you for completing the interview. Your responses have been submitted successfully.
            </p>
            
            <div className="bg-white/20 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-medium text-white mb-4">Summary:</h3>
              <div className="text-white/90 space-y-2">
                <p>Total Questions: {progressData?.total_questions}</p>
                <p>Questions Answered: {progressData?.answered_questions}</p>
                <p>Completion: {progressData?.progress_percentage}%</p>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => window.close()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium text-lg transition-colors duration-200"
              >
                Close Window
              </button>
              <p className="text-white/60 text-sm">
                This page will automatically refresh shortly to invalidate the link.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading question screen
  if (interviewStarted && !currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Loading question {currentQuestionIndex + 1}...</p>
        </div>
      </div>
    );
  }

  const totalQuestions = progressData?.total_questions || interviewInfo?.interview?.total_questions || 0;
  const isLastQuestion = (currentQuestionIndex + 1) >= totalQuestions;
  const isAnswerSubmitted = submittedAnswers.has(currentQuestion?.id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
      {/* Recording indicator */}
      {isRecording && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-4 py-2 rounded-full flex items-center space-x-2 z-50">
          <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
          <span>Recording - {formatTime(recordingTime)}</span>
        </div>
      )}

      {/* Progress bar */}
      <div className="absolute top-4 left-4 right-4 z-40">
        <div className="bg-white/20 rounded-full h-2">
          <div
            className="bg-green-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressData?.progress_percentage || ((currentQuestionIndex + 1) / totalQuestions * 100) || 0}%` }}
          />
        </div>
        <p className="text-white/80 text-sm mt-2">
          Question {currentQuestionIndex + 1} of {totalQuestions}
        </p>
      </div>

      {/* Main content */}
      <div className="flex items-center justify-center min-h-screen pt-16 pb-4 px-4">
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Video feed */}
          <div>
            <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
              <Webcam
                ref={webcamRef}
                audio={false}
                className="w-full h-80 object-cover rounded-lg"
                mirrored={true}
              />
            </div>
          </div>

          {/* Question and controls */}
          <div>
            <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {currentQuestion?.question_text}
                </h2>
                <p className="text-white/70 text-sm">
                  Type: {currentQuestion?.question_type}
                  {currentQuestion?.skill_assessed && (
                    <span> • Skill: {currentQuestion.skill_assessed}</span>
                  )}
                </p>
              </div>

              {/* Transcript input - COMMENTED OUT AS REQUESTED */}
              {/* <div className="mb-6">
                <textarea
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  placeholder="Your answer will be transcribed here, or you can type notes..."
                  className="w-full h-24 px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 disabled:opacity-50"
                  disabled={isAnswerSubmitted || isSubmittingAudio}
                />
              </div> */}

              {/* Recording controls - TEXT SUBMIT OPTION REMOVED */}
              {!isAnswerSubmitted && (
                <div className="flex items-center justify-center space-x-4 mb-8">
                  {!isRecording ? (
                    <button
                      onClick={startRecording}
                      disabled={isSubmittingAudio}
                      className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <MicrophoneIcon className="h-5 w-5" />
                      <span>Start Recording</span>
                    </button>
                  ) : (
                    <button
                      onClick={stopRecording}
                      disabled={isSubmittingAudio}
                      className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <StopIcon className="h-5 w-5" />
                      <span>{isSubmittingAudio ? 'Processing...' : 'Stop & Submit'}</span>
                    </button>
                  )}
                </div>
              )}

              {/* Audio submission loading indicator */}
              {isSubmittingAudio && (
                <div className="flex items-center justify-center space-x-3 mb-6 text-white">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span className="text-sm">Submitting please wait...</span>
                </div>
              )}

              {/* Navigation controls */}
              {isAnswerSubmitted && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-green-300">
                    <CheckIcon className="h-5 w-5" />
                    <span>Answer submitted</span>
                  </div>

                  <div className="flex space-x-3">
                    {isLastQuestion ? (
                      <button
                        onClick={handleCompleteInterview}
                        className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
                      >
                        <CheckIcon className="h-5 w-5" />
                        <span>Complete Interview</span>
                      </button>
                    ) : (
                      <button
                        onClick={handleNextQuestion}
                        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
                      >
                        <span>Next Question</span>
                        <ForwardIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Skip option for unanswered questions */}
              {!isAnswerSubmitted && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={handleSkipQuestion}
                    disabled={isSubmittingAudio}
                    className="text-white/60 hover:text-white text-sm px-4 py-2 rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingAudio ? 'Processing...' : 'Skip Question'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectInterview;